/**
 * TokenSecretModal — v2 replacement for TokenSecretDialog.js. Same
 * job: one-time display of the plain-text PAT after creation, with
 * a copy-to-clipboard control and a warning that this is the last
 * time it'll be visible.
 *
 * Uses the redesign Modal primitive. Backdrop click + Escape are
 * non-dismissive (handled by the parent — Modal doesn't close on
 * those by default for this use).
 */
import React, { useState } from 'react';
import { Modal, Button, IconButton, cn } from '../../../redesign';

const CopyIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function TokenSecretModal({ open, plainTextToken, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainTextToken || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = plainTextToken || '';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal
      open={open}
      // Intentionally not wired to onClose — admins must acknowledge
      // they saved the token via the footer button.
      onClose={() => { /* noop on backdrop / esc */ }}
      eyebrow="API Keys"
      title="API key created"
      maxWidth={560}
      footer={
        <Button variant="coral" onClick={onClose}>
          I've saved my token
        </Button>
      }
    >
      <div
        className={cn(
          'border italic',
        )}
        style={{
          borderRadius: 8,
          borderColor: '#E58A6A',
          background: '#FFF3EE',
          color: '#8A4A35',
          padding: '10px 14px',
          fontSize: 12.5,
          marginBottom: 14,
        }}
      >
        Store this token securely. You won't be able to view it again.
      </div>

      <div
        className="flex items-center"
        style={{
          gap: 8,
          padding: '8px 8px 8px 14px',
          background: '#FAF4EA',
          border: '1px solid #E8E0CF',
          borderRadius: 8,
        }}
      >
        <code
          className="font-mono flex-1 min-w-0 truncate text-navy"
          style={{ fontSize: 12, userSelect: 'all' }}
          title={plainTextToken}
        >
          {plainTextToken || ''}
        </code>
        <IconButton
          size="sm"
          aria-label="Copy token"
          title={copied ? 'Copied!' : 'Copy to clipboard'}
          onClick={handleCopy}
        >
          {copied ? CheckIcon : CopyIcon}
        </IconButton>
      </div>
    </Modal>
  );
}
